import { ProgramFamily, ProgramType } from 'Program';
import * as Yup from 'yup';
import textIsMostlyCjk from 'Utils/textIsMostlyCjk';
import { isNonRelayEmail } from 'EmailInput';
import { type FormConfig } from './FormConfigs.types';

// The age range basic_info accepts. basic_info_form_dir.js derives the `min` and `max` attributes on
// its date input from the same two numbers.
export const MIN_APPLICANT_AGE = 13;
export const MAX_APPLICANT_AGE = 100;

// write-text-about caps the fun fact on student_network_settings, and the email input caps its own
// length. The template passes the same numbers to those directives.
export const PERSONAL_FACT_MAX_CHARS = 50;
export const STUDENT_NETWORK_EMAIL_MAX_CHARS = 255;

// The need-statement limits on tuition_and_scholarships. The template passes the same numbers to
// write-text-about.
export const SHORT_ANSWER_SCHOLARSHIP_MIN_CHARS = 250;
export const SHORT_ANSWER_SCHOLARSHIP_MAX_CHARS = 500;

// The collection sizes more_about_you asks for. The template passes the same numbers to multi-select
// and add-an-item, which is what the FIXME comments there used to warn about.
export const TOP_LIST_SIZE = 3;
export const MIN_AWARDS_AND_INTERESTS = 2;

// The most legal names the education step accepts, the user's own name included. The template passes
// the same number to add-an-item.
export const MAX_LEGAL_NAMES = 5;

// The most responsibilities a work experience accepts. The template passes the same number to
// add-an-item.
export const MAX_RESPONSIBILITIES = 3;

// The name of the language test below. The error list groups this failure on its own, so it can ask
// the user to answer in English rather than tell them the field is incomplete. The field holds an
// answer; the answer is in the wrong language. See getSchemaFieldErrors in edit_career_profile_dir.
export const IS_NOT_MOSTLY_CJK_TEST = 'is-not-mostly-cjk';

// A free-text answer rendered by write-text-about. That directive asks for English and rejects an
// answer that is mostly CJK. This applies the same rule, and both read one predicate, so the schema
// and the angular validator cannot drift.
//
// The test passes for an empty answer, so `required` alone decides whether a blank answer is allowed.
const shortAnswer = () =>
    // Nullable, so an optional answer accepts null. `required()` on top of this still rejects null.
    Yup.string()
        .nullable()
        .test(IS_NOT_MOSTLY_CJK_TEST, 'Please answer in English', value => !textIsMostlyCjk(value));

// Adds the length limits write-text-about applies to a short answer.
//
// That directive skips an empty answer for both limits and lets `required` decide that case. Yup's
// own `min` rejects an empty string, so the minimum is a test rather than `.min()`. Its `max` already
// accepts an empty string, so that one is `.max()`.
const withLengthLimits = (
    schema: ReturnType<typeof shortAnswer>,
    { minChars, maxChars }: { minChars: number; maxChars: number },
) =>
    schema
        .max(maxChars)
        .test(
            'meets-the-minimum-length',
            `Please write at least ${minChars} characters`,
            value => !value || value.length >= minChars,
        );

// The template resolves an application_questions `localeKey` against this namespace, so the error
// list shows the same label the user reads above each input. See application_questions_form.html.
const withApplicationQuestionsErrorLabels = <T extends Record<string, { localeKey: string }>>(inputs: T) =>
    Object.fromEntries(
        Object.entries(inputs).map(([key, config]) => [
            key,
            {
                ...config,
                errorLabelKey: `careers.edit_career_profile.application_questions_form.${config.localeKey}`,
            },
        ]),
    ) as { [K in keyof T]: T[K] & { errorLabelKey: string } };

// application_questions is the same step in every program family. Only two of its labels change, so a
// factory keeps the four copies in step and lets the schema read its length limits straight from
// `inputs`. Before this, the limits lived only in the template attributes.
const buildApplicationQuestionsFormConfig = ({
    whyPursuingLocaleKey,
    priorExperienceLocaleKey,
}: {
    whyPursuingLocaleKey: string;
    priorExperienceLocaleKey: string;
}) => {
    const inputs = withApplicationQuestionsErrorLabels({
        shortAnswerWhyPursuing: { localeKey: whyPursuingLocaleKey, maxCharLength: 450, required: true },
        shortAnswerUseSkillsToAdvance: {
            localeKey: 'short_answer_use_skills_to_advance',
            maxCharLength: 450,
            required: true,
        },
        shortAnswerPriorExperience: { localeKey: priorExperienceLocaleKey, maxCharLength: 450, required: true },
        anythingElseToTellUs: { localeKey: 'anything_else_to_tell_us', maxCharLength: 500, required: false },
        // Rendered by select-with-other rather than write-text-about, so it has no length limit. The
        // step form uses `maxCharLength` to pick out the short answers it renders.
        how_did_you_hear_about_us: { localeKey: 'how_hear', required: true },
    });

    const shortAnswerFor = (
        key:
            | 'shortAnswerWhyPursuing'
            | 'shortAnswerUseSkillsToAdvance'
            | 'shortAnswerPriorExperience'
            | 'anythingElseToTellUs',
    ) => {
        const { maxCharLength, required } = inputs[key];
        const schema = shortAnswer().max(maxCharLength);
        return required ? schema.required() : schema;
    };

    return {
        stepName: 'application_questions',
        inputs,
        validationSchema: Yup.object({
            how_did_you_hear_about_us: Yup.string().required(),
            anythingElseToTellUs: shortAnswerFor('anythingElseToTellUs'),
            shortAnswerPriorExperience: shortAnswerFor('shortAnswerPriorExperience'),
            shortAnswerUseSkillsToAdvance: shortAnswerFor('shortAnswerUseSkillsToAdvance'),
            shortAnswerWhyPursuing: shortAnswerFor('shortAnswerWhyPursuing'),
        }),
    };
};

// One of the "pick your top three" lists on more_about_you. multi-select enforces the same count.
const topList = () => Yup.array().of(Yup.string().required()).min(TOP_LIST_SIZE).max(TOP_LIST_SIZE).required();

// When student_network_settings asks for a preferred email: the user keeps a full profile in a
// network they can see, and the address on their account cannot be used. The privacy setting is on
// the model, and the other two facts are published by the step form.
//
// Every argument is absent outside the form, so this returns false there. That is right: an account
// that needs an address already has one.
const preferredEmailIsRequired = (privacy: string, hasAccess: boolean, accountEmailIsValid: boolean) =>
    privacy === 'full' && hasAccess === true && accountEmailIsValid === false;

// The two checks the preferred email input applies, and an address must pass both.
//
// `type="email"` applies the first. It is a copy of EMAIL_REGEXP from angular 1.8.3, and it is
// anchored at both ends. The `email-validation` directive applies the second. That regex has no `^`
// anchor, so it checks only the end of the address. Do not anchor it here: it would then reject
// local-part characters such as `'` that angular accepts.
const ANGULAR_EMAIL_REGEXP =
    /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
const EMAIL_VALIDATION_DIRECTIVE_REGEXP = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,13}$/i;
const isValidStudentNetworkEmail = (address: string) =>
    ANGULAR_EMAIL_REGEXP.test(address) && EMAIL_VALIDATION_DIRECTIVE_REGEXP.test(address);

// The preferred email text the format rules judge: the text in the input while the step form
// publishes it, and the saved value otherwise. See the student_network_email rule below.
const studentNetworkEmailText = (value: string | null | undefined, parent: Record<string, unknown>) => {
    const viewValue = parent.student_network_email_view_value;
    return typeof viewValue === 'string' ? viewValue : value;
};

const basicInfoFormConfig = {
    stepName: 'basic_info',
    // The label for each schema path, so the error list can name a field without reading the DOM.
    //
    // `place_id` and `place_details` share the location label on purpose. One location control sets
    // both, so the error list shows one entry. See getSchemaFieldErrors in edit_career_profile_dir.
    inputs: {
        birthdate: { errorLabelKey: 'careers.edit_career_profile.basic_info_form.birthdate', required: true },
        name: { errorLabelKey: 'careers.edit_career_profile.basic_info_form.name', required: true },
        nickname: { errorLabelKey: 'careers.edit_career_profile.basic_info_form.nickname', required: true },
        place_details: {
            errorLabelKey: 'careers.edit_career_profile.basic_info_form.location',
            required: true,
            fieldPath: 'place_id',
        },
        place_id: { errorLabelKey: 'careers.edit_career_profile.basic_info_form.location', required: true },
        survey_highest_level_completed_education_description: {
            errorLabelKey: 'careers.edit_career_profile.education_form.my_highest_level_education',
            required: true,
        },
        survey_most_recent_role_description: {
            errorLabelKey: 'careers.edit_career_profile.work_form.my_most_recent_role',
            required: true,
        },
        survey_years_full_time_experience: {
            errorLabelKey: 'careers.edit_career_profile.work_form.i_have_this_many_years_experience',
            required: true,
        },
        // Gender is optional, so `sex` has no `required: true`. It reports an error only when the
        // user chooses to self-describe and types nothing. The label matches the input's placeholder,
        // which is what the older angular-driven list showed for this field.
        sex: {
            errorLabelKey: 'careers.edit_career_profile.demographics_form_section.identify_placeholder',
            required: false,
        },
    },
    validationSchema: Yup.object({
        // The applicant must be between 13 and 100 years old, and `required` alone enforces that.
        //
        // date-with-fallback checks its `min` and `max` attributes inside setViewValueAndNgModel,
        // and writes `null` to the model for a date outside the range. So a birthdate the user types
        // outside the range never reaches this schema, and `required` reports it as a missing
        // birthdate. That is what angular did too.
        //
        // Do not add an age test over the model here. The directive checks the range only when the
        // user edits the input, never when it derives its view value from a stored birthdate during
        // link. An age test over the model would block an account whose stored birthdate is already
        // out of range, which is a save angular allowed. See MIN_APPLICANT_AGE and MAX_APPLICANT_AGE,
        // which basic_info_form_dir.js turns into those two attributes.
        birthdate: Yup.string().required(),
        name: Yup.string().required(),
        nickname: Yup.string().required(),
        place_details: Yup.object()
            .required()
            // The location control keeps the previously resolved place on the model while the user
            // types, so `place_id` and `place_details` both look valid for text that resolves to
            // nothing. Compare against the text on screen instead.
            //
            // `location_view_value` is a transient form value, so it is absent outside the form. The
            // test passes when it is absent, which is right: a saved place was resolved when it was
            // saved. See transientFormValues in edit_career_profile_dir.js.
            .test('matches-the-location-on-screen', 'Select a location from the list', (value, context) => {
                const viewValue = context.parent.location_view_value;
                if (viewValue === undefined) return true;
                return viewValue === (value as { formatted_address?: string })?.formatted_address;
            }),
        place_id: Yup.string().required(),
        survey_highest_level_completed_education_description: Yup.string().required(),
        survey_most_recent_role_description: Yup.string().required(),
        survey_years_full_time_experience: Yup.string().required(),
        // Gender is optional. Choosing self-describe sets `sex` to null and reveals a text input, so
        // the model alone cannot tell "self-describing, nothing typed yet" from "never answered".
        // Only the first is invalid, and `sex_selection` is what separates them.
        //
        // `sex_selection` is a transient form value. When it is absent, this falls to `otherwise` and
        // passes, which is right for a saved profile: `sex` either holds text or was never answered.
        sex: Yup.string()
            .nullable()
            .when('sex_selection', {
                is: 'self_describe',
                then: schema => schema.required(),
            }),
    }),
};

// education_experience_detail renders a row under the formal degree heading only when `degree_program`
// is exactly true. The non-degree list takes the rows where it is exactly false.
const isFormalEducationExperience = (educationExperience: unknown) =>
    (educationExperience as { degree_program?: boolean } | undefined)?.degree_program === true;

// The inputs the formal degree block of education_experience_detail marks required.
const formalEducationExperienceSchema = Yup.object({
    // Name the two keys rather than taking the object alone. The step form replaces a cleared
    // organization with `{}`, and a bare `Yup.object().required()` accepts that, so neither the name
    // nor the country was checked. organization-autocomplete is required by default, and the country
    // select is marked required.
    educational_organization: Yup.object({
        text: Yup.string().required(),
        country: Yup.string().required(),
    }).required(),
    // getDegreeConfig always returns a config, so the degree select renders on every formal row.
    degree: Yup.string().required(),
    degree_level: Yup.string().required(),
    // The template reveals the two year selects only after the graduation questions are answered.
    // Until then the has_graduated or will_graduate rule fails as well, and all of these failures
    // collapse into one error list entry for the section.
    graduation_year: Yup.number().required(),
    start_year: Yup.number().required(),
    // `required` rejects a missing answer and accepts `false`, which is the answer "no, not yet".
    has_graduated: Yup.boolean().required(),
    // The template reveals this question only after the user answers "no" to has_graduated.
    will_graduate: Yup.boolean().when('has_graduated', {
        is: false,
        then: schema => schema.required(),
        otherwise: schema => schema.nullable().notRequired(),
    }),
    major: Yup.string().required(),
});

// The inputs the non-degree block of education_experience_detail marks required. That block has no
// country, degree level or graduation questions.
const nonDegreeEducationExperienceSchema = Yup.object({
    // Named `text`, for the same reason as the formal row. organization-autocomplete is required by
    // default, and the non-degree block does not pass `is-required`.
    educational_organization: Yup.object({ text: Yup.string().required() }).required(),
    major: Yup.string().required(),
    // The "have you completed" select. `required` accepts `false`, which is the answer "yes".
    will_not_complete: Yup.boolean().required(),
    graduation_year: Yup.number().required(),
});

// Picks the rules for one education_experiences row.
//
// A locked formal row has no rules. education_experience_detail_dir.js sets `$$disableEdit` on a
// formal row once admissions can require its official transcript, and every input on a locked row
// is `ng-required="!educationExperience.$$disableEdit"`. The user cannot edit those inputs, so a rule
// over them could block the save with no way to fix it.
const educationExperienceSchema = (educationExperience: unknown) => {
    if (!isFormalEducationExperience(educationExperience)) return nonDegreeEducationExperienceSchema;
    if ((educationExperience as { $$disableEdit?: boolean }).$$disableEdit) return Yup.object();
    return formalEducationExperienceSchema;
};

// The education step form publishes this key as true. The strict rules above apply only while it is
// true, because they describe the inputs on the page.
//
// Outside the form, the schema judges saved values, and the settings progress map and the submit
// gate read the result. Many saved formal rows have a null `degree_level`, because the backfill
// skipped write-in and unmapped degrees, and those students cannot see the problem from settings.
// `$$disableEdit` is also absent from a saved row, so a locked row would fail permanently. So saved
// values keep the rules that applied before the save gate moved to the schema.
const EDUCATION_STEP_ON_SCREEN = 'education_step_on_screen';

// The work and tuition_and_scholarships step forms publish these keys as true, for the same reason.
// A rule that arrived with the save gate applies to saved values only when the saved data already
// meets it. See the work date rule and the need statement limits.
const WORK_STEP_ON_SCREEN = 'work_step_on_screen';
const TUITION_STEP_ON_SCREEN = 'tuition_step_on_screen';

// The same condition as highestLevelEducationRequiresFormalEducation in edit_career_profile_helper.js.
// An empty level does not require a formal row. The education step then shows the
// has_no_formal_education checkbox, as it does for high school.
const formalEducationIsRequired = (highestLevelEducation: unknown) =>
    !!highestLevelEducation && highestLevelEducation !== 'high_school';

// The rows while the education step is on the page.
const onScreenEducationExperiencesSchema = (highestLevelEducation: unknown) => {
    const schema = Yup.array().of(Yup.lazy(educationExperienceSchema));
    if (!formalEducationIsRequired(highestLevelEducation)) return schema;

    // The hidden `hasEducationExperience` input requires a formal row, and it checks `degree_program`
    // rather than `degree`. A locked row can have no degree, and it still counts.
    return schema.test((education_experiences, { createError, path }) => {
        if (education_experiences?.some(isFormalEducationExperience)) return true;

        return createError({
            path,
            type: 'education_experiences',
            message: 'Must have at least one formal education experience',
        });
    });
};

// The saved rows. These are the rules from before the save gate moved to the schema.
const savedEducationExperiencesSchema = (highestLevelEducation: unknown) => {
    if (highestLevelEducation === 'high_school') return Yup.array().notRequired();

    return Yup.array()
        .of(
            Yup.object({
                educational_organization: Yup.object().required(),
                degree: Yup.string().nullable(),
                graduation_year: Yup.number().required(),
                major: Yup.string().required(),
                start_year: Yup.number().when('degree', {
                    is: (value: string) => !!value,
                    then: schema => schema.required(),
                    otherwise: schema => schema.nullable().notRequired(),
                }),
            }),
        )
        .test((education_experiences, { createError, path }) => {
            if (education_experiences?.some(educationExperience => educationExperience.degree)) return true;

            return createError({
                path,
                type: 'education_experiences',
                message: 'Must have at least one formal education experience',
            });
        });
};

const educationFormConfig = {
    stepName: 'education',
    inputs: {
        // The hidden `hasEducationExperience` input carries this requirement in the template, and it
        // sits under the formal degree heading.
        education_experiences: {
            errorLabelKey: 'careers.edit_career_profile.education_form.formal_degree_programs',
            required: true,
        },
        has_no_formal_education: {
            errorLabelKey: 'careers.edit_career_profile.education_form.has_no_formal_education',
            required: false,
        },
        legal_names: { errorLabelKey: 'careers.edit_career_profile.education_form.legal_names', required: true },
        native_english_speaker: {
            errorLabelKey: 'careers.edit_career_profile.education_form.native_english_speaker',
            required: true,
        },
        earned_accredited_degree_in_english: {
            errorLabelKey: 'careers.edit_career_profile.education_form.earned_accredited_degree_in_english',
            required: false,
        },
    },
    validationSchema: Yup.object({
        // add-an-item caps the list through its own `max`, which set a `required-range` error that
        // edit_career_profile_dir never collected. This matches that.
        //
        // `required` on an array rejects a missing list and accepts an empty one. Yup 1.x does not add a
        // length test to `required`; `min(1)` is what rejects `[]`. Yup 0.32 did add one, so do not read
        // this rule against the older docs.
        //
        // The `max-length` on the input is a per-item character limit that stops the item being added,
        // rather than a validation, so it has no rule here.
        //
        // The cap includes the user's own name. education_form_dir.js appends that name to the list,
        // and add-an-item compares the full list length against its `max`. So the user can add
        // MAX_LEGAL_NAMES - 1 other names, and the template shows its over-the-limit message above
        // MAX_LEGAL_NAMES entries.
        legal_names: Yup.array().of(Yup.string()).max(MAX_LEGAL_NAMES).required(),
        // If the user has indicated that they are not a native English speaker, we require they answer
        // whether or not they've earned an accredited degree in a predominantly English speaking institution.
        earned_accredited_degree_in_english: Yup.boolean().when('native_english_speaker', {
            is: false,
            then: schema => schema.required(),
            // This is nullable because we explicitly set it to `null` when/if the user indicates they're
            // a native English speaker.
            otherwise: schema => schema.nullable().notRequired(),
        }),
        // When the user has indicated they've completed more than a high school education, we require
        // at least one formal education experience.
        //
        // On the page, every row is checked against the inputs the form renders for it, whatever the
        // highest level of education. Saved values keep the earlier rules. See EDUCATION_STEP_ON_SCREEN.
        education_experiences: Yup.array().when(
            ['survey_highest_level_completed_education_description', EDUCATION_STEP_ON_SCREEN],
            ([highestLevelEducation, educationStepOnScreen]: unknown[]) =>
                educationStepOnScreen === true
                    ? onScreenEducationExperiencesSchema(highestLevelEducation)
                    : savedEducationExperiencesSchema(highestLevelEducation),
        ),
        // When the user has indicated that they've only completed a high school education, we require them
        // to check a checkbox indicating they have no "formal" education. The checkbox also shows when the
        // user has not answered the highest level of education, so the rule applies then too.
        //
        // The checkbox leaves the page once the user adds a formal row, and education_form_dir.js then
        // sets this to `false`. So the rule applies only while no formal row exists, which is the
        // condition that renders the checkbox.
        //
        // `isTrue`, not `required`. The column is `boolean default(FALSE), not null`, so the value is
        // never null and `required` would accept an unticked box. Angular's checkbox `required` blocked
        // on `false` alone, and `isTrue` matches that: it rejects `false` and ignores an absent key.
        //
        // The rule applies only while the education step is on the page. The earlier rule was
        // `required`, which a saved value always passes, so saved values keep passing. See
        // EDUCATION_STEP_ON_SCREEN.
        has_no_formal_education: Yup.boolean().when(
            ['survey_highest_level_completed_education_description', 'education_experiences', EDUCATION_STEP_ON_SCREEN],
            {
                is: (
                    survey_highest_level_completed_education_description: string,
                    education_experiences: unknown[] | undefined,
                    educationStepOnScreen: boolean | undefined,
                ) =>
                    educationStepOnScreen === true &&
                    !formalEducationIsRequired(survey_highest_level_completed_education_description) &&
                    !education_experiences?.some(isFormalEducationExperience),
                then: schema => schema.isTrue(),
                otherwise: schema => schema.notRequired(),
            },
        ),
        native_english_speaker: Yup.boolean().required(),
    }),
};

// The inputs work_experience_detail marks required on each row.
const workExperienceSchema = Yup.object({
    employment_type: Yup.string().required(),
    featured: Yup.boolean().notRequired(),
    industry: Yup.string().required(),
    job_title: Yup.string().required(),
    // Name `text` rather than taking the object alone, for the same reason as educational_organization
    // above: the step form replaces a cleared organization with `{}`, which a bare
    // `Yup.object().required()` accepts.
    professional_organization: Yup.object({ text: Yup.string().required() }).required(),
    // add-an-item sets `required-range` outside `min` and `max`. The template passes MAX_RESPONSIBILITIES
    // as `max`.
    responsibilities: Yup.array().of(Yup.string().required()).min(1).max(MAX_RESPONSIBILITIES).required(),
    role: Yup.string().required(),
    start_date: Yup.number().required(),
});

// A work row while the work step is on the page.
//
// choose-a-date passes each date to the other as `max-date` and `min-date`, and sets both invalid when
// the end is not after the start. An empty end date means the job is current.
//
// The rule applies only on the page. Several hundred completed profiles have a saved row whose end
// date is not after its start date, and choose-a-date checks the dates only when the user edits one.
//
// `end_date` has no field rule, because choose-a-date writes a Date for it and a number for the start.
// `Number()` reads both as milliseconds.
const onScreenWorkExperienceSchema = workExperienceSchema.test(
    'ends-after-it-starts',
    'The end date must be after the start date',
    workExperience => {
        const { start_date: startDate, end_date: endDate } = workExperience as {
            start_date?: number;
            end_date?: number | Date | null;
        };
        if (endDate == null || startDate == null) return true;
        return Number(endDate) > Number(startDate);
    },
);

const buildWorkFormConfig = ({ requireSalaryRange = true }: { requireSalaryRange?: boolean } = {}) => ({
    stepName: 'work',
    inputs: {
        // The hidden `hasWorkExperience` input and the featured-experience radio both carry this
        // requirement in the template, and both sit under the work experience heading.
        work_experiences: { errorLabelKey: 'careers.edit_career_profile.work_form.work_experience', required: true },
        employer_funding_eligibility: {
            errorLabelKey: 'careers.edit_career_profile.application_questions_form.employer_funding',
            required: true,
        },
        // work_form.html passes this to salary-survey-form-section, so the field is only marked
        // required where the schema requires it. An enrolled student can have no salary range.
        salary_range: {
            errorLabelKey: 'careers.edit_career_profile.salary_survey_form_section.total_salary',
            required: requireSalaryRange,
        },
    },
    validationSchema: Yup.object({
        employer_funding_eligibility: Yup.string().required(),
        ...(requireSalaryRange && { salary_range: Yup.string().required() }),
        work_experiences: Yup.array()
            .when([WORK_STEP_ON_SCREEN], ([workStepOnScreen]: unknown[], schema) =>
                schema.of(workStepOnScreen === true ? onScreenWorkExperienceSchema : workExperienceSchema),
            )
            .test((work_experiences, { createError, path }) => {
                const hasExactlyOneFeaturedWorkExperience =
                    work_experiences?.filter(work_experience => work_experience.featured).length === 1;
                const hasAtLeastOneFullTimeWorkExperience = work_experiences?.find(
                    work_experience => work_experience.employment_type === 'full_time',
                );

                if (hasExactlyOneFeaturedWorkExperience && hasAtLeastOneFullTimeWorkExperience) return true;

                return createError({
                    path,
                    type: 'work_experiences',
                    message: 'Must have exactly one featured and at least one full time work experience',
                });
            }),
    }),
});

const workFormConfig = buildWorkFormConfig();

const tuitionAndScholarshipsFormConfig = {
    stepName: 'tuition_and_scholarships',
    inputs: {
        // The radio group has one screen-reader heading rather than a visible label, so both the
        // radios and the checkboxes behind them point at it.
        declineScholarshipConsideration: {
            errorLabelKey:
                'careers.edit_career_profile.tuition_and_scholarships_form.decline_or_apply_for_scholarships',
            required: true,
        },
        scholarshipLabelsInterestedIn: {
            errorLabelKey:
                'careers.edit_career_profile.tuition_and_scholarships_form.decline_or_apply_for_scholarships',
            required: false,
        },
        shortAnswerScholarship: {
            errorLabelKey: 'careers.edit_career_profile.tuition_and_scholarships_form.need_statement_desc',
            required: false,
        },
    },
    validationSchema: Yup.object({
        declineScholarshipConsideration: Yup.boolean().required(),
        // If the user has not declined scholarship consideration, we require they select at least
        // one scholarship they're interested in.
        //
        // This never blocks a save. The save interceptor in tuition_and_scholarships_form_dir.js opens
        // the no-selection modal first, and that modal either declines for the user or keeps the
        // step open. The rule keeps a legacy record in that state from counting as complete, and so
        // from passing the submission gate.
        scholarshipLabelsInterestedIn: Yup.array().when('declineScholarshipConsideration', {
            is: false,
            then: schema => schema.of(Yup.string().required()).min(1).required(),
            otherwise: schema => schema.notRequired(),
        }),
        // If the user selects the need-based scholarship, we require they answer shortAnswerScholarship.
        //
        // write-text-about caps this between 250 and 500 characters. Both limits set errors that
        // edit_career_profile_dir never collected, so a too-short or too-long statement blocked the
        // save with nothing in the error list.
        //
        // The limits apply only while the tuition step is on the page. A reapplication reuses the form
        // datum of the earlier application, and many saved statements are longer than 500
        // characters, because the limit arrived after they were written. The submit gate judges saved
        // values, so a limit there would block those applicants until they reopened this step. Saved
        // values keep the earlier rule, which is `required` alone.
        shortAnswerScholarship: shortAnswer().when(
            ['scholarshipLabelsInterestedIn', TUITION_STEP_ON_SCREEN],
            ([scholarshipLabelsInterestedIn, tuitionStepOnScreen]: unknown[], schema) => {
                const limited =
                    tuitionStepOnScreen === true
                        ? withLengthLimits(schema, {
                              minChars: SHORT_ANSWER_SCHOLARSHIP_MIN_CHARS,
                              maxChars: SHORT_ANSWER_SCHOLARSHIP_MAX_CHARS,
                          })
                        : schema;
                // The list is absent outside the form for a user with no ProgramFamilyFormDatum.
                const needSelected = (scholarshipLabelsInterestedIn as string[] | undefined)?.includes('need');
                return needSelected ? limited.required() : limited;
            },
        ),
    }),
};

// There are (currently) no required inputs on this page, instead we consider the action of submitting an application
// as required to consider this step complete.
// See also FormCompletionHelper module, getFirstIncompleteForm and getFormProgress functions, specifically the 'submit_application'
// carve-out in getFormProgress.
const submitFormConfig = {
    stepName: 'submit_application',
};

// If all program types in a program family share the same application forms, that config should go here.
export const ApplicationFormConfigsByProgramFamily: Record<
    Extract<
        ProgramFamily,
        ProgramFamily.master_of_business_administration | ProgramFamily.master_of_science_in_business_analytics
    >,
    FormConfig[]
> = {
    [ProgramFamily.master_of_business_administration]: [
        basicInfoFormConfig,
        educationFormConfig,
        workFormConfig,
        buildApplicationQuestionsFormConfig({
            whyPursuingLocaleKey: 'short_answer_why_pursuing_master_of_business_administration',
            priorExperienceLocaleKey: 'short_answer_prior_experience_master_of_business_administrations',
        }),
        tuitionAndScholarshipsFormConfig,
        submitFormConfig,
    ],
    [ProgramFamily.master_of_science_in_business_analytics]: [
        basicInfoFormConfig,
        educationFormConfig,
        workFormConfig,
        buildApplicationQuestionsFormConfig({
            whyPursuingLocaleKey: 'short_answer_why_pursuing_master_of_science_in_business_analytics',
            priorExperienceLocaleKey: 'short_answer_prior_experience_master_of_science_in_business_analytics',
        }),
        tuitionAndScholarshipsFormConfig,
        submitFormConfig,
    ],
};

// If a program type has specific application forms, that config should go here.
export const ApplicationFormConfigsByProgramType: Record<
    Extract<ProgramType, ProgramType.msse | ProgramType.msaie>,
    FormConfig[]
> = {
    [ProgramType.msse]: [
        basicInfoFormConfig,
        educationFormConfig,
        workFormConfig,
        buildApplicationQuestionsFormConfig({
            whyPursuingLocaleKey: 'short_answer_why_pursuing_master_of_science_in_software_engineering',
            priorExperienceLocaleKey: 'short_answer_prior_experience_master_of_science_in_software_engineering',
        }),
        tuitionAndScholarshipsFormConfig,
        submitFormConfig,
    ],
    [ProgramType.msaie]: [
        basicInfoFormConfig,
        educationFormConfig,
        workFormConfig,
        buildApplicationQuestionsFormConfig({
            whyPursuingLocaleKey: 'short_answer_why_pursuing_master_of_science_in_ai_engineering',
            priorExperienceLocaleKey: 'short_answer_prior_experience_master_of_science_in_ai_engineering',
        }),
        tuitionAndScholarshipsFormConfig,
        submitFormConfig,
    ],
};

// Configs for forms that are shown to the user in order to complete their student profile.
// Note there is some overlap here with ApplicationFormConfigsByProgramFamily - specifically the education and work forms.
// The work form config is built without salary_range validation here because enrolled students may have
// null salary_range and shouldn't be blocked from completing their student profile. work_form.html
// reads `inputs.salary_range.required` so the field is not marked required here either. On the
// application it stays required.
export const StudentProfileFormConfigs: FormConfig[] = [
    basicInfoFormConfig,
    {
        stepName: 'more_about_you',
        inputs: {
            avatar_url: {
                errorLabelKey: 'careers.edit_career_profile.profile_photo_form_section.profile_photo',
                required: true,
            },
            awards_and_interests: {
                errorLabelKey: 'careers.edit_career_profile.more_about_you_form.awards_and_interests',
                required: true,
            },
            top_personal_descriptors: {
                errorLabelKey: 'careers.edit_career_profile.more_about_you_form.my_attributes',
                required: true,
            },
            top_motivations: {
                errorLabelKey: 'careers.edit_career_profile.more_about_you_form.my_motivations',
                required: true,
            },
            top_workplace_strengths: {
                errorLabelKey: 'careers.edit_career_profile.more_about_you_form.my_strengths',
                required: true,
            },
            // Only degree program applicants answer this one. See is_degree_program below.
            primary_reason_for_applying: {
                errorLabelKey:
                    'careers.edit_career_profile.more_about_you_form.primary_reason_for_applying_post_application',
                required: false,
            },
        },
        validationSchema: Yup.object({
            avatar_url: Yup.string().required(),
            awards_and_interests: Yup.array()
                .of(
                    Yup.object({
                        text: Yup.string().required(),
                    }),
                )
                .min(MIN_AWARDS_AND_INTERESTS)
                .required(),
            // multi-select asks for exactly TOP_LIST_SIZE of each. It caps the count through its own
            // `max`, which set a `required-range` error that never reached the error list.
            top_motivations: topList(),
            top_personal_descriptors: topList(),
            top_workplace_strengths: topList(),
            // Required for a degree program applicant, and the input is hidden for everyone else.
            // `is_degree_program` is published by the step form, because it comes from the cohort
            // rather than from either form model. Absent means not required, which is right outside
            // the form.
            primary_reason_for_applying: Yup.string()
                .nullable()
                .when('is_degree_program', {
                    is: true,
                    then: schema => schema.required(),
                }),
        }),
    },
    educationFormConfig,
    buildWorkFormConfig({ requireSalaryRange: false }),
    {
        stepName: 'demographics',
        // Every question on this step is optional, which is what the step heading tells the user.
        // The step still needs a schema, because one input inside it is conditionally required. See
        // `optional` on FormConfig for why that combination needs a flag.
        optional: true,
        inputs: {
            // The label matches the input's placeholder, the same choice basic_info makes for the
            // self-described gender answer.
            self_described_race: {
                errorLabelKey: 'careers.edit_career_profile.demographics_form_section.identify_placeholder',
                required: false,
            },
        },
        validationSchema: Yup.object({
            // Race is optional, and "other" is the one answer that needs a written value. Selecting
            // it reveals a text input, and demographics_form_section_dir.js drops a blank answer
            // before it reaches `race`. So the model shows an empty list for both "selected other,
            // typed nothing" and "answered nothing", and only the first is invalid.
            //
            // `race_includes_other` and `self_described_race` are transient form values, because
            // neither has a column. When they are absent this passes, which is right for a saved
            // profile: `race` either holds the written answer or the user never gave one. See
            // transientFormValues in edit_career_profile_dir.js.
            //
            // demographics_form_section.html marks the input `required`, and angular used to block
            // the save on it. That stopped when the schema took over the gate, so this rule is what
            // keeps the answer from being dropped without a word to the user.
            self_described_race: Yup.string()
                .nullable()
                .when('race_includes_other', {
                    is: true,
                    then: schema => schema.required(),
                }),
        }),
    },
    {
        stepName: 'student_network_settings',
        requiresVisit: true,
        inputs: {
            personal_fact: {
                errorLabelKey: 'careers.edit_career_profile.student_network_settings_form.fun_fact',
                required: true,
            },
            pref_student_network_privacy: {
                errorLabelKey: 'student_network.student_network.privacy_settings',
                required: true,
            },
            student_network_email: {
                errorLabelKey: 'student_network.student_network.preferred_email',
                required: false,
            },
        },
        validationSchema: Yup.object({
            // write-text-about caps the fun fact at 50 characters. That set a `maxlength` error which
            // edit_career_profile_dir never collected, so an over-long answer blocked the save with
            // nothing in the error list.
            personal_fact: shortAnswer().required().max(PERSONAL_FACT_MAX_CHARS),
            pref_student_network_privacy: Yup.string().required(),
            // Two rules, and both read values the step form publishes. See preferredEmailIsRequired
            // above and transientFormValues in edit_career_profile_dir.js.
            //
            // As with every rule that reads a published value, both pass when the keys are absent.
            // That is right outside the form: an account that needs an email already has one, or the
            // user has not reached this step yet.
            student_network_email: Yup.string()
                .nullable()
                // The format rules belong to the input, so they apply only while the input is on the
                // page. student_network_settings_form.html renders that row on
                // `studentNetworkEmailRequired || currentUser.student_network_email`, and these four
                // published values are the same condition.
                //
                // Without this the save can block on a field the user cannot see. Type an address
                // while the row is required, then set privacy to hidden: the row goes away, the typed
                // value stays on the model, and the error list names a field with nothing to scroll
                // to. Angular could not do that, because removing the input removes its validity.
                .when(
                    [
                        'pref_student_network_privacy',
                        'has_access_to_full_student_network_profiles',
                        'account_email_is_valid_for_student_network',
                        'account_has_student_network_email',
                    ],
                    {
                        is: (
                            privacy: string,
                            hasAccess: boolean,
                            accountEmailIsValid: boolean,
                            accountHasEmail: boolean,
                        ) =>
                            preferredEmailIsRequired(privacy, hasAccess, accountEmailIsValid) ||
                            accountHasEmail === true,
                        // The format rules judge the text in the input rather than the model. Angular
                        // writes `undefined` to the model for an address it rejects, so the model
                        // alone cannot show an invalid address. The step form publishes the text as
                        // `student_network_email_view_value`. When that key is absent, which is the
                        // case outside the form, the rules judge the saved value.
                        //
                        // Do not set `allowInvalid` on the input to get the text into the model. Then
                        // a malformed address typed into a row that later leaves the page reaches the
                        // server, because these rules skip a row that is not on the page.
                        then: schema =>
                            schema
                                .test(
                                    'is-at-most-the-maximum-length',
                                    `Please use at most ${STUDENT_NETWORK_EMAIL_MAX_CHARS} characters`,
                                    (value, context) => {
                                        const text = studentNetworkEmailText(value, context.parent);
                                        return !text || text.length <= STUDENT_NETWORK_EMAIL_MAX_CHARS;
                                    },
                                )
                                .test('is-a-valid-address', 'Please enter a valid email', (value, context) => {
                                    const text = studentNetworkEmailText(value, context.parent);
                                    return !text || isValidStudentNetworkEmail(text);
                                })
                                .test('is-not-a-relay-address', 'Please use a non-relay email', (value, context) => {
                                    const text = studentNetworkEmailText(value, context.parent);
                                    return !text || !isNonRelayEmail(text);
                                }),
                    },
                )
                .when(
                    [
                        'pref_student_network_privacy',
                        'has_access_to_full_student_network_profiles',
                        'account_email_is_valid_for_student_network',
                    ],
                    {
                        is: preferredEmailIsRequired,
                        then: schema => schema.required(),
                    },
                ),
        }),
    },
];
